import { syncCloudProjectIfConfigured } from '@/features/project-cloud/api'
import { serializeVirtualResources } from '@/features/virtual-resource/persistence'
import { normalizeProjectAudioEventPaths } from '@/features/virtual-resource/audioEventPaths'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import {
  BITMAP_FONT_FOLDER_SEGMENTS,
} from '@/features/bitmap-font/runtime'
import type MainProject from '@/model/MainProject'
import DataFileMap from '@/model/Resource/DataFileMap'
import BrowserUtil from '@/platform/oss/browserUtil'
import { PROJECT_MALL_NEW_FILE_NAME, writeMallNewBin } from '@/features/project-mall/runtime'
import { upsertCloudProjectIndexItem } from '@/features/project-cloud/ossList'
import {
  PROJECT_CURRENT_DATA_DIR,
  PROJECT_CURRENT_DIR,
  PROJECT_CURRENT_INFO,
  PROJECT_CURRENT_MALL_NEW,
  PROJECT_CURRENT_PROJECT_BIN,
  PROJECT_CURRENT_RES_JSON,
} from '@/features/project-cloud/ossPaths'

export type ProjectOssSaveProgress = {
  stage: 'prepare' | 'project-bin' | 'mall-bin' | 'virtual-resources' | 'project-info'
  fileName: string
  current: number
  total: number
  percent: number
  detail?: string
}

export type ProjectOssSaveResult = {
  guid: string
  rootDir: string
  uploadedKeys: string[]
  uploadedFontCount: number
}

type SaveRoots = Partial<Record<ResourceCategoryKey, DataFileMap | null>>

type OssUploadProgress = {
  fileName: string
  percent: number
}

type ProjectInfoOptions = {
  publishGindex?: string | number | null
}

function buildProjectIndexItem(project: MainProject, projectInfoPath: string) {
  return {
    projectKey: project.projectHeader.ID.trim(),
    projectName: project.storyTree.ProjectName?.trim() || '未命名工程',
    updatedAt: Date.now(),
    resolution: `${project.screenHash.width} × ${project.screenHash.height}`,
    projectInfoPath,
  }
}

function trimSlash(value = ''): string {
  return String(value).replace(/^\/+|\/+$/g, '')
}

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value)
}

function formatProjectInfoDate(date = new Date()): string {
  return [
    `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`,
    `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`,
  ].join(' ')
}

/**
 * 生成云端工程列表读取用的工程信息文本。
 * @param project 当前主工程。
 * @param options 需要跟随云端草稿持久化的轻量发布元信息。
 * @returns 兼容旧工具 projectInfo.txt 的文本内容。
 */
export function buildProjectInfoText(project: MainProject, options?: ProjectInfoOptions): string {
  const name = project.storyTree.ProjectName?.trim() || '未命名工程'
  const screen = `${project.screenHash.width}*${project.screenHash.height}`
  const date = formatProjectInfoDate()
  const version =
    project.projectHeader.Version ||
    project.projectConstant.gameVersion ||
    project.dataVer ||
    0
  const publishGindex = String(options?.publishGindex ?? '').trim()

  const lines = [
    '{',
    `    name:"${name}",`,
    `    screen:"${screen}",`,
    `    date:"${date}",`,
    `    version:${version}${publishGindex ? ',' : ''}`,
  ]
  if (publishGindex) {
    lines.push(`    gindex:"${publishGindex}"`)
  }
  lines.push('}')
  return lines.join('\n')
}

/** 将云端工程信息文本写入 OSS 的 current/projectInfo.txt。 */
export async function writeProjectInfoToOss(params: {
  project: MainProject
  uid?: number | string | null
  publishGindex?: string | number | null
}): Promise<string> {
  const guid = params.project.projectHeader.ID?.trim()
  if (!guid) {
    throw new Error('工程缺少 guid，无法写入 projectInfo.txt')
  }
  const oss = BrowserUtil.getInstance()
  if (params.uid !== undefined && params.uid !== null && String(params.uid).trim()) {
    oss.connect(params.uid)
  }
  const projectInfoPath = `${guid}/${PROJECT_CURRENT_INFO}`
  await oss.writeData(projectInfoPath, buildProjectInfoText(params.project, {
    publishGindex: params.publishGindex,
  }), {
    contentType: 'text/plain;charset=utf-8',
  })
  await upsertCloudProjectIndexItem(buildProjectIndexItem(params.project, projectInfoPath))
  return projectInfoPath
}

function stripBitmapFontVirtualRoot(root: DataFileMap | null | undefined): DataFileMap | null {
  if (!root) return null
  const nextRoot = new DataFileMap(root.toJson())
  nextRoot.children = nextRoot.children.filter((child) =>
    !(child.isFolder && child.name === BITMAP_FONT_FOLDER_SEGMENTS[0]),
  )
  return nextRoot
}

function stripBitmapFontVirtualResources(roots: SaveRoots): SaveRoots {
  return {
    ...roots,
    font: stripBitmapFontVirtualRoot(roots.font),
  }
}

/**
 * 仅将虚拟资源映射表上传到 OSS `current/res.json`。
 * 用于本地/素材库导入资源后同步网盘映射，无需整包保存工程。
 */
export async function uploadVirtualResourceRootsToOss(params: {
  project: MainProject
  virtualResourceRoots: SaveRoots
  uid?: number | string | null
}): Promise<string> {
  const guid = params.project.projectHeader.ID?.trim()
  if (!guid) {
    throw new Error('工程缺少 guid，无法上传 res.json')
  }
  const oss = BrowserUtil.getInstance()
  if (params.uid !== undefined && params.uid !== null && String(params.uid).trim()) {
    oss.connect(params.uid)
  } else {
    oss.initDefaultConfig()
  }
  if (!oss.isConfigured()) {
    throw new Error('OSS 未配置，请先设置 OSS 连接参数后再保存')
  }
  if (!oss.getRootDir()) {
    throw new Error('OSS rootfolder 未设置，请先登录后再保存')
  }

  await oss.createFolder(guid)
  await oss.createFolder(`${guid}/${PROJECT_CURRENT_DIR}`)

  normalizeProjectAudioEventPaths(params.project, params.virtualResourceRoots.audio)
  const savedVirtualResourceRoots = stripBitmapFontVirtualResources(params.virtualResourceRoots)
  const resJsonText = serializeVirtualResources(savedVirtualResourceRoots)
  const resJsonPath = `${guid}/${PROJECT_CURRENT_RES_JSON}`
  await oss.uploadFile(new Blob([resJsonText], { type: 'application/json;charset=utf-8' }), {
    objectKey: resJsonPath,
    fileName: 'res.json',
  })
  return resJsonPath
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
}

/**
 * 将当前工程主包、虚拟资源映射、字体资源和 `projectInfo.txt` 同步到 OSS。
 * 这是浏览器版云端保存的主入口。
 */
export class ProjectOssSaveService {
  async save(params: {
    project: MainProject
    virtualResourceRoots: SaveRoots
    uid?: number | string | null
    publishGindex?: string | number | null
    projectBinBytes?: Uint8Array
    onProgress?: (progress: ProjectOssSaveProgress) => void
  }): Promise<ProjectOssSaveResult> {
    const guid = params.project.projectHeader.ID?.trim()
    if (!guid) {
      throw new Error('工程缺少 guid，无法保存到 OSS')
    }
    const oss = BrowserUtil.getInstance()
    if (params.uid !== undefined && params.uid !== null && String(params.uid).trim()) {
      oss.connect(params.uid)
    } else {
      oss.initDefaultConfig()
    }
    if (!oss.isConfigured()) {
      throw new Error('OSS 未配置，请先设置 OSS 连接参数后再保存')
    }

    const rootDir = oss.getRootDir()
    if (!rootDir) {
      throw new Error('OSS rootfolder 未设置，请先登录后再保存')
    }

    const uploadedKeys: string[] = []
    const emit = (progress: ProjectOssSaveProgress) => {
      params.onProgress?.(progress)
    }

    emit({
      stage: 'prepare',
      fileName: guid,
      current: 0,
      total: 4,
      percent: 0,
      detail: '准备 OSS 保存目录',
    })

    await oss.createFolder(guid)
    await oss.createFolder(`${guid}/${PROJECT_CURRENT_DIR}`)
    await oss.createFolder(`${guid}/${PROJECT_CURRENT_DATA_DIR}`)

    normalizeProjectAudioEventPaths(params.project, params.virtualResourceRoots.audio)
    const projectBinBytes = params.projectBinBytes ?? params.project.writeBinBuffer()
    const projectBinBlob = new Blob([toArrayBuffer(projectBinBytes)], { type: 'application/octet-stream' })
    const projectBinPath = `${guid}/${PROJECT_CURRENT_PROJECT_BIN}`
    await oss.uploadFile(projectBinBlob, {
      objectKey: projectBinPath,
      fileName: 'projectS.bin',
      onProgress: (progress: OssUploadProgress) => {
        emit({
          stage: 'project-bin',
          fileName: progress.fileName,
          current: 1,
          total: 4,
          percent: progress.percent,
          detail: '上传工程二进制',
        })
      },
    })
    uploadedKeys.push(projectBinPath)

    const mallNewBytes = writeMallNewBin(params.project)
    const mallNewPath = `${guid}/${PROJECT_CURRENT_MALL_NEW}`
    await oss.uploadFile(new Blob([toArrayBuffer(mallNewBytes)], { type: 'application/octet-stream' }), {
      objectKey: mallNewPath,
      fileName: PROJECT_MALL_NEW_FILE_NAME,
      onProgress: (progress: OssUploadProgress) => {
        emit({
          stage: 'mall-bin',
          fileName: progress.fileName,
          current: 2,
          total: 4,
          percent: progress.percent,
          detail: '上传商城数据',
        })
      },
    })
    uploadedKeys.push(mallNewPath)

    normalizeProjectAudioEventPaths(params.project, params.virtualResourceRoots.audio)
    const savedVirtualResourceRoots = stripBitmapFontVirtualResources(params.virtualResourceRoots)
    const resJsonPath = await uploadVirtualResourceRootsToOss({
      project: params.project,
      virtualResourceRoots: savedVirtualResourceRoots,
      uid: params.uid,
    })
    uploadedKeys.push(resJsonPath)
    emit({
      stage: 'virtual-resources',
      fileName: 'res.json',
      current: 3,
      total: 4,
      percent: 100,
      detail: '上传虚拟资源映射',
    })

    const projectInfoText = buildProjectInfoText(params.project, {
      publishGindex: params.publishGindex,
    })
    const projectInfoPath = `${guid}/${PROJECT_CURRENT_INFO}`
    await oss.uploadFile(new Blob([projectInfoText], { type: 'text/plain;charset=utf-8' }), {
      objectKey: projectInfoPath,
      fileName: 'projectInfo.txt',
      onProgress: (progress: OssUploadProgress) => {
        emit({
          stage: 'project-info',
          fileName: progress.fileName,
          current: 4,
          total: 4,
          percent: progress.percent,
          detail: '上传工程信息',
        })
      },
    })
    uploadedKeys.push(projectInfoPath)
    await upsertCloudProjectIndexItem(buildProjectIndexItem(params.project, projectInfoPath))

    await syncCloudProjectIfConfigured({
      project: params.project,
      virtualResourceRoots: savedVirtualResourceRoots,
    })

    emit({
      stage: 'project-info',
      fileName: 'projectInfo.txt',
      current: 4,
      total: 4,
      percent: 100,
      detail: 'OSS 保存完成',
    })

    return {
      guid,
      rootDir,
      uploadedKeys,
      uploadedFontCount: 0,
    }
  }
}

export const projectOssSaveService = new ProjectOssSaveService()
